import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { Role } from '@/models/AccountEnums';
import { waServicesQuery } from './baseQueries';
import type { WARootState } from './store';

export interface FirmInvestorRecord {
  InvestorId: number;
  InvestorName?: string;
  EstateId?: number | null;
  InvestorHHId?: number | null;
  UserId?: number | null;
}

export interface FirmInvestorsRequest {
  searchTerm?: string;
  page?: number;
  limit?: number;
  AlwaysShowHouseholds?: boolean;
}

/** Tree node returned by /api/firm/AdvisorGroupsTree. */
export interface AdvisorGroupTreeNode {
  AdvisorGroupId?: number;
  Name?: string;
  Children?: AdvisorGroupTreeNode[];
}

export interface AdvisorGroupsTreeResponse {
  Records?: AdvisorGroupTreeNode[];
}

export interface AdvisorGroupsTreeResult {
  tree: AdvisorGroupTreeNode[];
}

/** Record returned by /api/firm/AdvisorGroups/{id}/Permissions. */
export interface AdvisorGroupPermissionRecord {
  Code: string;
  Label?: string;
  Permission: boolean;
  Role?: string;
  AdvisorGroupId?: number | null;
  AdvisorGroupName?: string | null;
}

interface AdvisorGroupPermissionsResponse {
  Records?: AdvisorGroupPermissionRecord[];
  TotalLength?: number;
}

/** Subset of AdvisorGroupModel returned by /api/firm/AdvisorGroups/{id}. */
export interface FirmAdvisorGroupModel {
  AdvisorGroupId?: number;
  Name?: string;
  ShowAwayAccountsByDefault?: boolean;
}

export interface SaveClientPermissionsArgs {
  advisorGroupId: number;
  /** Changed permission records, round-tripped from getClientPermissions. */
  permissions: AdvisorGroupPermissionRecord[];
  /** Firm-wide share-by-default; omit when unchanged. Saved atomically with the permissions. */
  showAwayAccountsByDefault?: boolean;
}

export const firmApi = createApi({
  reducerPath: 'firmApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AdvisorGroupsTree', 'ClientPermissions', 'AdvisorGroup'],
  endpoints: (builder) => ({
    getFirmInvestors: builder.query<
      RecordResponse<FirmInvestorRecord>,
      FirmInvestorsRequest
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        if (state.auth.currentRole !== Role.Firm) {
          return {
            error: {
              status: 403,
              data: 'Firm investors are only available for firm role',
            } as FetchBaseQueryError,
          };
        }

        const params: Record<string, string | number | boolean> = {
          AlwaysShowHouseholds: args.AlwaysShowHouseholds ?? true,
        };
        if (args.searchTerm) {
          params.searchTerm = args.searchTerm;
        }
        if (args.page !== undefined) {
          params.page = args.page;
        }
        if (args.limit !== undefined) {
          params.limit = args.limit;
        }

        const result = await baseQuery({
          url: '/api/firm/investors',
          method: 'GET',
          params,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<FirmInvestorRecord> };
      },
    }),
    getAdvisorGroupsTree: builder.query<AdvisorGroupsTreeResult, undefined>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/api/firm/AdvisorGroupsTree',
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        const raw = result.data as AdvisorGroupsTreeResponse | undefined;
        const records = raw?.Records;
        if (raw === undefined || !Array.isArray(records)) {
          const status = result.meta?.response?.status ?? 200;
          return {
            error: {
              status: 'PARSING_ERROR',
              originalStatus: status,
              data: result.data,
              error: 'Advisor groups response could not be parsed',
            } as FetchBaseQueryError,
          };
        }
        return { data: { tree: records } };
      },
      providesTags: ['AdvisorGroupsTree'],
    }),
    getClientPermissions: builder.query<
      Record<string, AdvisorGroupPermissionRecord>,
      number
    >({
      query: (advisorGroupId) => ({
        url: `/api/firm/AdvisorGroups/${String(advisorGroupId)}/Permissions`,
        method: 'GET',
      }),
      transformResponse: (response: AdvisorGroupPermissionsResponse) =>
        (response.Records ?? [])
          .filter((record) => record.Role === 'Investor' && record.Code)
          .reduce<Record<string, AdvisorGroupPermissionRecord>>(
            (byCode, record) => {
              byCode[record.Code] = record;
              return byCode;
            },
            {},
          ),
      providesTags: (_result, _error, advisorGroupId) => [
        { type: 'ClientPermissions', id: String(advisorGroupId) },
      ],
    }),
    getFirmAdvisorGroup: builder.query<FirmAdvisorGroupModel, number>({
      query: (advisorGroupId) => ({
        url: `/api/firm/AdvisorGroups/${String(advisorGroupId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, advisorGroupId) => [
        { type: 'AdvisorGroup', id: String(advisorGroupId) },
      ],
    }),
    saveClientPermissions: builder.mutation<
      AdvisorGroupPermissionsResponse,
      SaveClientPermissionsArgs
    >({
      query: ({ advisorGroupId, permissions, showAwayAccountsByDefault }) => ({
        url: `/api/firm/AdvisorGroups/${String(advisorGroupId)}/Permissions`,
        method: 'PUT',
        body: {
          Permissions: permissions,
          ShowAwayAccountsByDefault: showAwayAccountsByDefault,
        },
      }),
      // Invalidate on error too so a failed save refetches server truth.
      invalidatesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'ClientPermissions', id: String(advisorGroupId) },
        { type: 'AdvisorGroup', id: String(advisorGroupId) },
      ],
    }),
  }),
});

export const {
  useGetFirmInvestorsQuery,
  useLazyGetFirmInvestorsQuery,
  useGetAdvisorGroupsTreeQuery,
  useGetClientPermissionsQuery,
  useGetFirmAdvisorGroupQuery,
  useSaveClientPermissionsMutation,
} = firmApi;
